import { z } from 'zod';

export const CONSTRUCTION_STAGES = [
  'planning',
  'land_prep',
  'foundation',
  'structure',
  'roofing',
  'plumbing_electrical',
  'walls_plastering',
  'interior',
  'finishing',
  'completed',
] as const;

export const constructionStageSchema = z.enum(CONSTRUCTION_STAGES);
export type ConstructionStage = z.infer<typeof constructionStageSchema>;

export const stageMetaSchema = z.object({
  id: constructionStageSchema,
  order: z.number().int().nonnegative(),
  label: z.string(),
  description: z.string(),
  accentColor: z.string(),
});
export type StageMeta = z.infer<typeof stageMetaSchema>;

export const STAGE_META: Readonly<Record<ConstructionStage, StageMeta>> = {
  planning: {
    id: 'planning',
    order: 0,
    label: 'Planning',
    description: 'Blueprints, permits, and dreaming out loud.',
    accentColor: '#8B8778',
  },
  land_prep: {
    id: 'land_prep',
    order: 1,
    label: 'Land Preparation',
    description: 'Clearing, leveling, marking the plot.',
    accentColor: '#A79373',
  },
  foundation: {
    id: 'foundation',
    order: 2,
    label: 'Foundation',
    description: 'Concrete goes in — the invisible spine.',
    accentColor: '#6E6455',
  },
  structure: {
    id: 'structure',
    order: 3,
    label: 'Structure',
    description: 'Columns, beams, slabs rising.',
    accentColor: '#5A5142',
  },
  roofing: {
    id: 'roofing',
    order: 4,
    label: 'Roofing',
    description: 'Getting closed in from above.',
    accentColor: '#7B6F5C',
  },
  plumbing_electrical: {
    id: 'plumbing_electrical',
    order: 5,
    label: 'Plumbing & Electrical',
    description: 'Hidden veins and arteries.',
    accentColor: '#8A7A63',
  },
  walls_plastering: {
    id: 'walls_plastering',
    order: 6,
    label: 'Walls & Plastering',
    description: 'Skin over the bones.',
    accentColor: '#B4A488',
  },
  interior: {
    id: 'interior',
    order: 7,
    label: 'Interior',
    description: 'Tiles, joinery, paint — rooms become rooms.',
    accentColor: '#C7B79A',
  },
  finishing: {
    id: 'finishing',
    order: 8,
    label: 'Finishing',
    description: 'Fixtures, furniture, final polish.',
    accentColor: '#D8CCB4',
  },
  completed: {
    id: 'completed',
    order: 9,
    label: 'Completed',
    description: 'Home.',
    accentColor: '#4C6B4A',
  },
};

export const stageProgress = (stage: ConstructionStage): number =>
  Math.round((STAGE_META[stage].order / (CONSTRUCTION_STAGES.length - 1)) * 100);
